import { useEffect, useState } from 'react';
import { API_BASE } from '../client/config';
import { Logo } from './Logo';

export type OrganizationBrandingData = {
  orgId: string;
  orgName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  isBo: boolean;
  canManage: boolean;
};

export const DEFAULT_BRANDING_TITLE = 'BRICK lit — Admin';
export const DEFAULT_BRANDING_FAVICON = `${import.meta.env.BASE_URL}favicon.svg`;

export function brandingAssetUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, `${API_BASE.replace(/\/$/, '')}/`);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch { return null; }
}

function faviconLink(): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (existing) return existing;
  const link = document.createElement('link');
  link.rel = 'icon'; document.head.append(link);
  return link;
}

function restoreFavicon(): void {
  const link = faviconLink();
  if (link.getAttribute('href') !== DEFAULT_BRANDING_FAVICON) link.href = DEFAULT_BRANDING_FAVICON;
  if (link.type !== 'image/svg+xml') link.type = 'image/svg+xml';
  link.removeAttribute('sizes');
}

function restoreDefaults(): void {
  if (document.title !== DEFAULT_BRANDING_TITLE) document.title = DEFAULT_BRANDING_TITLE;
  restoreFavicon();
}

export function useOrganizationBranding(enabled = true) {
  const [branding, setBranding] = useState<OrganizationBrandingData | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let controller: AbortController | null = null;
    let favicon: HTMLImageElement | null = null;
    let current: OrganizationBrandingData | null = null;
    const cancelRequests = () => {
      controller?.abort();
      if (favicon) { favicon.onload = null; favicon.onerror = null; favicon = null; }
    };
    const clear = () => {
      cancelRequests(); current = null;
      restoreDefaults();
    };
    const reset = () => { clear(); setBranding(null); setLoading(false); setError(null); };
    const load = () => {
      cancelRequests(); setError(null);
      if (!enabled) { reset(); return; }
      const request = new AbortController(); controller = request;
      setLoading(true);
      void fetch(`${API_BASE}/api/branding/current`, { credentials: 'include', signal: request.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error(`브랜딩 조회 실패 (HTTP ${response.status})`);
          const data = await response.json() as OrganizationBrandingData;
          if (request.signal.aborted) return;
          const identityChanged = current !== null && (current.orgId !== data.orgId || current.isBo !== data.isBo);
          if (identityChanged) restoreDefaults();
          current = data;
          setBranding(data);
          if (data.isBo) { restoreDefaults(); return; }
          const title = data.orgName.trim() ? `${data.orgName} — Admin` : DEFAULT_BRANDING_TITLE;
          if (document.title !== title) document.title = title;
          const iconUrl = brandingAssetUrl(data.faviconUrl);
          if (!iconUrl) { restoreFavicon(); return; }
          if (!identityChanged && faviconLink().href === iconUrl) return;
          if (iconUrl) {
            const image = new Image(); favicon = image;
            image.onload = () => {
              if (request.signal.aborted) return;
              const link = faviconLink(); link.removeAttribute('type'); link.href = iconUrl;
            };
            image.onerror = () => {
              if (request.signal.aborted) return;
              restoreFavicon();
            };
            image.src = iconUrl;
          }
        }).catch((reason: unknown) => {
          if (!request.signal.aborted) {
            reset();
            setError(reason instanceof Error ? reason.message : String(reason));
          }
        }).finally(() => { if (!request.signal.aborted) setLoading(false); });
    };
    const authChanged = () => { reset(); load(); };
    load();
    window.addEventListener('bricklit:scope-changed', load);
    window.addEventListener('bricklit:auth-changed', authChanged);
    window.addEventListener('bricklit:logout', reset);
    window.addEventListener('pagehide', clear);
    return () => {
      window.removeEventListener('bricklit:scope-changed', load);
      window.removeEventListener('bricklit:auth-changed', authChanged);
      window.removeEventListener('bricklit:logout', reset);
      window.removeEventListener('pagehide', clear);
      clear();
    };
  }, [enabled]);

  return { branding: enabled ? branding : null, loading: enabled && loading, error };
}

export function OrganizationBranding({ size = 20, className = '', enabled = true }: {
  size?: number;
  className?: string;
  enabled?: boolean;
}): JSX.Element {
  const { branding } = useOrganizationBranding(enabled);
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  useEffect(() => { setFailedLogo(null); }, [branding]);
  const organization = enabled && branding && !branding.isBo ? branding : null;
  const logoUrl = brandingAssetUrl(organization?.logoUrl ?? null);
  return <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
    {logoUrl && failedLogo !== logoUrl ? <img key={`${organization?.orgId}:${logoUrl}`} src={logoUrl}
      alt={`${organization?.orgName ?? '조직'} 로고`} style={{ height: size, maxWidth: 180 }}
      className="block w-auto object-contain" onError={() => setFailedLogo(logoUrl)} /> : <Logo size={size} className="block shrink-0" />}
    {organization && <span className="max-w-[180px] truncate text-[14px] font-semibold text-text" title={organization.orgName}>{organization.orgName}</span>}
    {/* 로고 옆 '디자인 설정' 링크는 삭제했다(2026-09-16 대교). 조직 브랜딩은 운영사가
        고객사 상세(#/h9/tenants/:orgId/design)에서 다룬다 — 조직 Owner가 직접 바꾸지 않는다. */}
  </span>;
}
